/**
 * Cross-method blunder verdict.
 *
 * The two detection methods answer different questions — the combinatorial
 * search finds the rejection set that minimizes the maximum residual, while
 * iterative data snooping (Pope's tau test) tests residuals statistically.
 * Agreement between them is strong evidence of a real blunder; disagreement
 * is a prompt to inspect the point against field evidence. This module
 * reduces both result sets to that comparison, and reports as data when the
 * comparison cannot support a conclusion.
 */

import type {
  BlunderDetectionResults,
  DataSnoopingResults,
  PointPair,
} from '@/types';
import { DETECTION_METHOD_VERSION } from './blunderDetection';

export interface FlaggedPoint {
  id: number;
  label: string;
}

export type VerdictState =
  /** Neither method has been run. */
  | 'none'
  /** Only one method has results — no cross-check possible yet. */
  | 'single'
  /** Both ran; neither flags anything, and no limitation applies. */
  | 'clean'
  /**
   * Both ran and flag the identical non-empty set. Low power alone does not
   * undermine agreement — a rejection is still significant at alpha — so it
   * is carried in `limitations` as a completeness caveat only.
   */
  | 'agree'
  /** Both ran; the flagged sets differ (possibly one empty). */
  | 'differ'
  /**
   * Both ran and the flagged sets match, but a limitation means the match is
   * not evidence: both empty under a low-power test (no evidence of clean
   * data), or any match when redundancy ran out or results are outdated.
   * `agreed` still lists any pairs both methods flag.
   */
  | 'inconclusive';

export type VerdictLimitation =
  /** Data snooping stopped, or could not start, because redundancy r < 2. */
  | 'insufficientRedundancy'
  /**
   * The deciding tau test lacked power: a blunder of the reported MDB size
   * is more likely to pass than to be rejected, so a pass is weak evidence.
   */
  | 'lowPower'
  /**
   * A result came from an earlier detection version (normalized test
   * weights, Free pairs re-entering the fit, or normal critical values) and
   * may compare different point sets. Re-run detection.
   */
  | 'outdatedResults';

export interface BlunderVerdict {
  state: VerdictState;
  /** Best rejection set from the combinatorial search; null if not run. */
  combinatorial: FlaggedPoint[] | null;
  /** Rejected set from iterative data snooping; null if not run. */
  dataSnooping: FlaggedPoint[] | null;
  /** Flagged by both methods — high confidence. */
  agreed: FlaggedPoint[];
  /** Flagged by the combinatorial search only. */
  combinatorialOnly: FlaggedPoint[];
  /** Flagged by data snooping only. */
  dataSnoopingOnly: FlaggedPoint[];
  /** Why the results are weaker evidence than their flags suggest. */
  limitations: VerdictLimitation[];
}

// Prefer the pair's own label so verdict chips match the labels the
// detection engine prints in its result tables.
const labelFor = (id: number, pointPairs: PointPair[]): string => {
  const pair = pointPairs.find((p) => p.id === id);
  if (!pair) return String(id);
  if (pair.label) return pair.label;
  if (pair.sys1Point && pair.sys2Point) {
    return `${pair.sys1Point}-${pair.sys2Point}`;
  }
  return String(id);
};

const toFlagged = (ids: number[], pointPairs: PointPair[]): FlaggedPoint[] =>
  ids.map((id) => ({ id, label: labelFor(id, pointPairs) }));

/**
 * The combinatorial search's recommendation to compare against data
 * snooping.
 *
 * All-levels runs carry one best set per rejection level (summary is
 * level-ordered, and deeper levels always reduce the max residual just by
 * rejecting more), so there is no single cross-level "winner" without a
 * stopping rule. When `matchLevel` is given (IDS's own rejection count) the
 * comparison uses the combinatorial best at that same level — the
 * apples-to-apples question "rejecting k points, are they the same k?".
 * Without a matchLevel, the most conservative row (level 1) represents the
 * search. Single-level runs are sorted best-first; if the initial
 * no-rejection row ranks first, the recommendation is to reject nothing.
 */
export const bestCombinatorialSet = (
  results: BlunderDetectionResults | null,
  matchLevel?: number
): number[] | null => {
  if (!results || results.error) return null;
  if (results.isAllLevels) {
    const summary = results.summary;
    if (!summary || summary.length === 0) return null;
    if (matchLevel !== undefined) {
      const row = summary.find((s) => s.level === matchLevel);
      // IDS rejected more points than the deepest level searched — the
      // deepest available row is the closest comparison.
      return (row ?? summary[summary.length - 1]).rejectedIds;
    }
    return summary[0].rejectedIds;
  }
  const best = results.results?.[0];
  if (!best) return null;
  return best.isInitial ? [] : best.rejectedIds;
};

const LIMITATION_ORDER: VerdictLimitation[] = [
  'insufficientRedundancy',
  'lowPower',
  'outdatedResults',
];

/** Limitations carried by the two result sets, in a stable order. */
const collectLimitations = (
  blunderResults: BlunderDetectionResults | null,
  dataSnoopingResults: DataSnoopingResults | null
): VerdictLimitation[] => {
  const found = new Set<VerdictLimitation>();
  if (dataSnoopingResults?.insufficientRedundancy) {
    found.add('insufficientRedundancy');
  }
  if (dataSnoopingResults && !dataSnoopingResults.error) {
    if (dataSnoopingResults.lowPower) found.add('lowPower');
    if (
      dataSnoopingResults.methodVersion !== DETECTION_METHOD_VERSION ||
      dataSnoopingResults.testStatistic !== 'tau'
    ) {
      found.add('outdatedResults');
    }
  }
  if (
    blunderResults &&
    !blunderResults.error &&
    blunderResults.methodVersion !== DETECTION_METHOD_VERSION
  ) {
    found.add('outdatedResults');
  }
  return LIMITATION_ORDER.filter((limitation) => found.has(limitation));
};

export const deriveBlunderVerdict = (
  blunderResults: BlunderDetectionResults | null,
  dataSnoopingResults: DataSnoopingResults | null,
  pointPairs: PointPair[]
): BlunderVerdict => {
  const idsIds =
    dataSnoopingResults && !dataSnoopingResults.error
      ? dataSnoopingResults.finalRejectedIds
      : null;
  // Compare at IDS's rejection count when it rejected anything, so the
  // methods answer the same question ("which k points?").
  const matchLevel = idsIds && idsIds.length > 0 ? idsIds.length : undefined;
  const comboIds = bestCombinatorialSet(blunderResults, matchLevel);

  const combinatorial = comboIds ? toFlagged(comboIds, pointPairs) : null;
  const dataSnooping = idsIds ? toFlagged(idsIds, pointPairs) : null;
  const limitations = collectLimitations(blunderResults, dataSnoopingResults);

  if (!combinatorial && !dataSnooping) {
    return {
      state: 'none',
      combinatorial,
      dataSnooping,
      agreed: [],
      combinatorialOnly: [],
      dataSnoopingOnly: [],
      limitations,
    };
  }

  if (!combinatorial || !dataSnooping) {
    const only = combinatorial ?? dataSnooping ?? [];
    return {
      state: 'single',
      combinatorial,
      dataSnooping,
      agreed: [],
      combinatorialOnly: combinatorial ? only : [],
      dataSnoopingOnly: dataSnooping ? only : [],
      limitations,
    };
  }

  const comboSet = new Set(combinatorial.map((f) => f.id));
  const idsSet = new Set(dataSnooping.map((f) => f.id));
  const agreed = combinatorial.filter((f) => idsSet.has(f.id));
  const combinatorialOnly = combinatorial.filter((f) => !idsSet.has(f.id));
  const dataSnoopingOnly = dataSnooping.filter((f) => !comboSet.has(f.id));

  let state: VerdictState;
  if (combinatorialOnly.length > 0 || dataSnoopingOnly.length > 0) {
    state = 'differ';
  } else if (
    limitations.some((limitation) => limitation !== 'lowPower') ||
    (agreed.length === 0 && limitations.includes('lowPower'))
  ) {
    // Silence from a test that could not have flagged much is not evidence
    // of clean data. A significant rejection stays significant at low power,
    // so agreement on flagged pairs survives a lowPower caveat alone.
    state = 'inconclusive';
  } else {
    state = agreed.length === 0 ? 'clean' : 'agree';
  }

  return {
    state,
    combinatorial,
    dataSnooping,
    agreed,
    combinatorialOnly,
    dataSnoopingOnly,
    limitations,
  };
};
